import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Users, Settings } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyTeams({ user }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isParent = user?.roles?.includes("academy_parent");
  const isPlayer = user?.roles?.includes("academy_player");
  const isCoach = user?.roles?.includes("academy_coach");
  // Admins (convenor / captain / academy_admin) get a shortcut to the
  // Team Management page where they can create / edit / delete teams.
  const isAdmin = ["convenor", "captain", "academy_admin"].includes(user?.role);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      // If parent, get their children's teams only
      if (isParent && !isAdmin) {
        const childrenResponse = await axios.get(`${API}/academy/my-children`, {
          withCredentials: true
        });
        
        // Extract unique team IDs from children
        const teamIds = [...new Set(childrenResponse.data
          .map(child => child.team_id)
          .filter(id => id))]; // Filter out null/undefined
        
        // Get details for only those teams
        const allTeamsResponse = await axios.get(`${API}/academy/teams`);
        const filteredTeams = allTeamsResponse.data.filter(team => 
          teamIds.includes(team.id)
        );
        setTeams(filteredTeams);
      } else {
        // Players, coaches, admins can see all teams
        const response = await axios.get(`${API}/academy/teams`);
        setTeams(response.data);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Group teams by age group
  const groupedTeams = teams.reduce((acc, team) => {
    if (!acc[team.age_group]) {
      acc[team.age_group] = [];
    }
    acc[team.age_group].push(team);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy">
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Academy
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football Academy</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h2 className="text-4xl font-bold text-white">Academy Teams</h2>
          {isAdmin && (
            <Link to="/admin/academy-teams-management">
              <Button
                className="bg-amber-600 hover:bg-amber-500 text-white"
                data-testid="manage-teams-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Teams (Create / Edit)
              </Button>
            </Link>
          )}
        </div>

        {teams.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-slate-400 mb-4">No teams available yet.</p>
            {isAdmin && (
              <Link to="/admin/academy-teams-management">
                <Button
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                  data-testid="manage-teams-empty-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Create your first team
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.keys(groupedTeams).sort().map((ageGroup) => (
              <div key={ageGroup}>
                <h3 className="text-2xl font-bold text-white mb-4">{ageGroup}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedTeams[ageGroup].map((team) => (
                    <Link key={team.id} to={`/academy/teams/${team.id}`}>
                      <Card className="p-6 bg-slate-900/50 border-2 border-blue-500/30 hover:border-blue-500/30 transition-all cursor-pointer">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-white">{team.name}</h3>
                          <Users className="w-6 h-6 text-blue-400" />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Gender:</span>
                            <span className="font-semibold text-white capitalize">{team.gender}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Type:</span>
                            <span className="font-semibold text-white capitalize">{team.session_type}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Players:</span>
                            <span className="font-semibold text-blue-400">{team.player_count}</span>
                          </div>
                        </div>

                        <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700" size="sm">
                          View Team Details
                        </Button>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
